import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { FieldOrigin, isCustomOrigin, LoanFieldPropsProperties } from 'config'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { defaultAccessAccountTypes } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties'
import { visibilitySections } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/inputs'
import { validateLoanStructureData } from 'pages/LoanStructure/Logic'
import { getLoanRestrictionErrors } from 'pages/LoanSubmission/logic'
import { store } from 'reducers'
import { LoanType } from 'reducers/loanDetail.reducer'
import { changeObject2Array, isEmpty, reorderObject } from 'utils'
import { isVisibleFieldVisibleProps } from 'utils/loan'

import { BorrowerLabel, BorrowerSeperator } from './config'
import { borrowerMenuKey, menuKeyToFieldOrigin } from './util'

type menuType = {
  [key: string]: string
}

export const getAppBorrowerMenu = (
  borrowerApplication: boolean = false,
  customMenuTitles?: Record<string, string>,
  borrowerSeparator: string = BorrowerSeperator.BORROWER,
) => {
  const { loan, globalConfigReducer, auth, borrower } = store.getState()
  let accountType = auth.profile.temparary ? 'borrower' : auth.profile.accountType

  if (accountType == 'borrower') {
    const profileEmail = auth.profile.email
    Object.keys(borrower).forEach((borrowerSep) => {
      if (borrower[borrowerSep].email == profileEmail) accountType = borrowerSep
    })
  }
  let isCorrespondentLoan = loan.type == LoanType.CORRESPONDENT
  let isCaliforniaLoan = loan.propertyState === 'CA'
  const isBorrower = borrowerSeparator == BorrowerSeperator.BORROWER

  const loanFieldsProps = globalConfigReducer?.[GlobalConfigType.LoanFields] || {}
  const pageProps: LoanFieldPropsProperties = loanFieldsProps.properties || {}
  const customInfoOriginKeys = Object.keys(loanFieldsProps).filter((origin) => isCustomOrigin(origin))

  const isBorrowerSidebarSection = (fieldOrigin: FieldOrigin | string) => {
    const sectionType = pageProps[fieldOrigin as FieldOrigin]?.sectionType
    return sectionType === undefined || sectionType === 'Borrower'
  }

  let rlt: menuType = {
    borrower: customMenuTitles?.[FieldOrigin.BorrowerInformation] || 'Borrower Information',
    property: customMenuTitles?.[FieldOrigin.PropertyInformation] || 'Loan and Property Information',
    employment: customMenuTitles?.[FieldOrigin.EmploymentAndIncome] || 'Employment and Income',
    asset: customMenuTitles?.[FieldOrigin.AssetLiability] || 'Asset & Liability',
    track: customMenuTitles?.[FieldOrigin.TrackRecord] || 'Track Record & REO',
    hmda: customMenuTitles?.[FieldOrigin.DeclarationsHMDA] || 'Declarations & HMDA',
    sign: customMenuTitles?.['sign'] || 'Acknowledgement & Sign',
    credit: customMenuTitles?.['credit'] || 'Credit Score',
    additionalMember: customMenuTitles?.['additionalMember'] || 'Additional Entity Members',
    mlds: customMenuTitles?.['mlds'] || 'MLDS',
  }

  customInfoOriginKeys.forEach((customOriginKey) => {
    if (!isBorrowerSidebarSection(customOriginKey)) return
    rlt[customOriginKey] = customMenuTitles?.[customOriginKey] || 'Custom Information'
  })

  if (!isBorrower) delete rlt.property
  if (!isCaliforniaLoan || !isBorrower) delete rlt.mlds

  if (borrowerApplication || isCorrespondentLoan) delete rlt.credit

  const visibilityProps = loanFieldsProps['visibility'] || {}
  Object.keys(rlt).forEach((_fieldOrigin) => {
    const menuKey = isCustomOrigin(_fieldOrigin) ? _fieldOrigin : _fieldOrigin.replace(/\d+$/, '')
    let fieldOrigin = menuKeyToFieldOrigin(menuKey) as FieldOrigin

    for (const key in visibilitySections)
      if ((visibilitySections as any)[key] == menuKey) fieldOrigin = key as FieldOrigin

    const property = pageProps[fieldOrigin] || {}

    if (isCustomOrigin(fieldOrigin) && !isBorrowerSidebarSection(fieldOrigin)) {
      delete rlt[_fieldOrigin]
      return
    }
    if (
      visibilityProps[fieldOrigin] === false ||
      !(property.accountTypes || defaultAccessAccountTypes())?.[accountType] ||
      property.visibleLoanTypes?.[loan.type] === false ||
      property.visibleByBorrower?.[borrowerSeparator] === false
    ) {
      delete rlt[_fieldOrigin]
      return
    }

    if (!isEmpty(property.visibleByCondition)) {
      const restrictions = changeObject2Array(property.visibleByCondition)
      const errors = getLoanRestrictionErrors(restrictions)
      if (errors.length) delete rlt[_fieldOrigin]
    }

    let isVisible = true
    if (!isEmpty(property.visibleLogicByAccountTypes)) {
      const matchedVisibleLogicByAccountTypes = property.visibleLogicByAccountTypes?.filter((item) =>
        item.accountTypes?.includes(accountType),
      )

      if (matchedVisibleLogicByAccountTypes?.length) {
        isVisible = matchedVisibleLogicByAccountTypes.some((item) =>
          isVisibleFieldVisibleProps({
            visibleLogic: item.visibleLogic,
          }),
        )
      }
    }

    if (!isEmpty(property.visibleLogic) && isVisible) {
      isVisible = isVisibleFieldVisibleProps({
        visibleLogic: property.visibleLogic,
      })
    }

    if (!isVisible) delete rlt[_fieldOrigin]
  })
  removeNonPagePermissions(rlt, ['pipeline', 'loanApplication'])

  rlt = reorderObject(
    rlt,
    loanFieldsProps?.order?.map((v: FieldOrigin) => (visibilitySections as any)[v] || v),
  )

  if (isBorrower) return rlt

  let coBorrwerIdx = Object.values(BorrowerSeperator).indexOf(borrowerSeparator as any)
  coBorrwerIdx += 1

  const coBorrowerMenu: menuType = {}
  Object.keys(rlt).forEach((key) => {
    coBorrowerMenu[`${key}${coBorrwerIdx}`] = rlt[key]
  })

  return coBorrowerMenu
}

export const getAppCoBorrowerMenu = (
  borrowerApplication: boolean = false,
  borrowerSeparator: string = BorrowerSeperator.CO_BORROWER,
  customMenuTitles?: Record<string, string>,
) => {
  return getAppBorrowerMenu(borrowerApplication, customMenuTitles, borrowerSeparator)
}

const getMapKeys = () => {
  const { borrower, globalConfigReducer } = store.getState()
  const customMenuTitles = globalConfigReducer[GlobalConfigType.LoanFields]?.customTitle || {}
  const borrowerMenus = getAppBorrowerMenu(true, customMenuTitles)
  let rtl = { ...borrowerMenus }
  Object.keys(borrower)
    .filter((v) => v !== BorrowerSeperator.BORROWER)
    .map((key) => {
      const coBorrowerMenus = getAppCoBorrowerMenu(false, key, customMenuTitles)
      rtl = { ...rtl, ...coBorrowerMenus }
    })
  return rtl
}

const SignValidationMenus = ['property', 'employment', 'borrower', 'asset', 'track', 'hmda']

const getCustomOriginMenus = () => {
  const { globalConfigReducer } = store.getState()
  const loanFieldsProps = globalConfigReducer?.[GlobalConfigType.LoanFields] || {}
  return Object.keys(loanFieldsProps).filter((origin) => isCustomOrigin(origin))
}

const scoreValidateMenus = (borrowerSeparator: BorrowerSeperator) => {
  const customOrigins = getCustomOriginMenus()
  const menus = cloneDeep([...SignValidationMenus, ...customOrigins, 'sign'])
  if (borrowerSeparator != BorrowerSeperator.BORROWER) menus.splice(0, 1)
  menus.forEach((menu, index) => (menus[index] = borrowerMenuKey(borrowerSeparator, menu)))

  return menus
}

const signValidateMenus = (borrowerSeparator: BorrowerSeperator) => {
  const customOrigins = getCustomOriginMenus()
  const menus = cloneDeep([...SignValidationMenus, ...customOrigins])
  if (borrowerSeparator != BorrowerSeperator.BORROWER) menus.splice(0, 1)
  menus.forEach((menu, index) => (menus[index] = borrowerMenuKey(borrowerSeparator, menu)))

  return menus
}

export const pullScoreValidation = (borrowerSeperator: BorrowerSeperator): string[] => {
  const { step } = store.getState()
  const { application } = step
  let rlt: any = []
  const mapKeys = getMapKeys()
  scoreValidateMenus(borrowerSeperator).map((key: string) => {
    if (key.startsWith('asset') || !mapKeys[key]) return
    if (application[key] === -1) {
      let text = BorrowerLabel()[borrowerSeperator]
      rlt.push(`- ${mapKeys[key]} in ${text} need to be Completed!`)
    }
  })
  return rlt
}

export const signApplictionValidation = async (borrowerSeparator: BorrowerSeperator) => {
  const { step, borrower, loanDetail, loan } = store.getState()
  const { application } = step
  const { includeCoborrower } = loanDetail
  let borrowerKeys: BorrowerSeperator[] = [BorrowerSeperator.BORROWER]
  if (includeCoborrower) {
    const coBorrowerKeys = Object.keys(borrower).filter(
      (v) => v !== BorrowerSeperator.BORROWER && borrower[v].isIncluded,
    ) as BorrowerSeperator[]
    borrowerKeys.push(...coBorrowerKeys)
  }
  // let borrowerKeys: BorrowerSeperator[] = [borrowerSeparator]
  let rlt: any = []
  const mapKeys = getMapKeys()
  const loanStructureErrors = validateLoanStructureData(loan, loanDetail.rateData)
  rlt.push(...loanStructureErrors)
  borrowerKeys.map((borrowerSeperator) => {
    signValidateMenus(borrowerSeperator).map((key: string) => {
      if (key.startsWith('asset') || !mapKeys[key]) return
      if (application[key] === -1) {
        let text = `${BorrowerLabel()[borrowerSeperator]}'s`
        rlt.push(`${text} ${mapKeys[key]} Need to be Completed!`)
      }
    })
  })
  if (![LoanType.CORRESPONDENT, LoanType.TABLEFUNDER].includes(loanDetail.loanType)) {
    if (borrower.borrower?.agreeToSign !== true && borrowerSeparator === BorrowerSeperator.BORROWER)
      rlt.push(`Borrower 1 Need to Agree on Acknowledgement!`)
    if (includeCoborrower)
      Object.keys(borrower)
        .filter((v) => v !== BorrowerSeperator.BORROWER && borrower[v].isIncluded)
        .map((b) => {
          if (borrowerSeparator !== b) return
          if (borrower[b].agreeToSign) return
          rlt.push(`${BorrowerLabel()[b]} Need to Agree on Acknowledgement!`)
        })
  }
  return rlt
}
